import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const CurrencyContext = createContext(null);

// Sensible fallback rates (mid-2026 ballpark) so the UI works before the API responds.
const FALLBACK_RATES = {
  USD: 1.0,
  INR: 83.5,
  EUR: 0.92,
  GBP: 0.78,
  JPY: 150.0,
  HKD: 7.82,
  CAD: 1.36,
  AUD: 1.52,
};

export const CURRENCY_META = {
  USD: { symbol: '$',  code: 'USD', flag: '🇺🇸', locale: 'en-US', decimals: 2 },
  INR: { symbol: '₹',  code: 'INR', flag: '🇮🇳', locale: 'en-IN', decimals: 0 },
  EUR: { symbol: '€',  code: 'EUR', flag: '🇪🇺', locale: 'de-DE', decimals: 2 },
  GBP: { symbol: '£',  code: 'GBP', flag: '🇬🇧', locale: 'en-GB', decimals: 2 },
  JPY: { symbol: '¥',  code: 'JPY', flag: '🇯🇵', locale: 'ja-JP', decimals: 0 },
  HKD: { symbol: 'HK$',code: 'HKD', flag: '🇭🇰', locale: 'en-HK', decimals: 2 },
  CAD: { symbol: 'C$', code: 'CAD', flag: '🇨🇦', locale: 'en-CA', decimals: 2 },
  AUD: { symbol: 'A$', code: 'AUD', flag: '🇦🇺', locale: 'en-AU', decimals: 2 },
};

const STORAGE_KEY = 'teenvest.displayCurrency';

export const CurrencyProvider = ({ children }) => {
  const { user } = useAuth();
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [displayCcy, setDisplayCcyState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'USD'; } catch { return 'USD'; }
  });
  const [loaded, setLoaded] = useState(false);

  const setDisplayCcy = useCallback((ccy) => {
    setDisplayCcyState(ccy);
    try { localStorage.setItem(STORAGE_KEY, ccy); } catch { /* noop */ }
  }, []);

  // Fetch FX rates once we have an auth token (the endpoint is auth-protected).
  useEffect(() => {
    let cancelled = false;
    const fetchRates = async () => {
      if (!user) return;
      try {
        const res = await api.get('/currency/rates');
        if (cancelled) return;
        setRates({ ...FALLBACK_RATES, ...(res.data?.rates || {}) });
      } catch {
        /* keep fallbacks */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    fetchRates();
    return () => { cancelled = true; };
  }, [user?.id]);

  const value = useMemo(() => {
    const ccy = displayCcy in CURRENCY_META ? displayCcy : 'USD';
    const meta = CURRENCY_META[ccy];
    const rateFromUSD = rates[ccy] || FALLBACK_RATES[ccy] || 1;

    const convert = (usdAmount) => Number(usdAmount || 0) * rateFromUSD;

    const format = (usdAmount, opts = {}) => {
      const amount = convert(usdAmount);
      const decimals = opts.decimals ?? meta.decimals;
      try {
        return new Intl.NumberFormat(meta.locale, {
          style: 'currency',
          currency: meta.code,
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(amount);
      } catch {
        return `${meta.symbol}${amount.toFixed(decimals)}`;
      }
    };

    // Sign-aware format ("+₹1,234" or "-$1.23")
    const formatSigned = (usdAmount, opts = {}) => {
      const amount = convert(usdAmount);
      const s = amount >= 0 ? '+' : '-';
      return s + format(Math.abs(usdAmount), opts).replace(/^[-+]/, '');
    };

    // Format an amount that's already in a specific native currency (e.g. ₹1291 for RELIANCE.NS)
    // without going through USD. Useful for showing the native quote inline.
    const formatNative = (amount, nativeCcy = 'USD', opts = {}) => {
      const m = CURRENCY_META[nativeCcy] || CURRENCY_META.USD;
      const decimals = opts.decimals ?? m.decimals;
      try {
        return new Intl.NumberFormat(m.locale, {
          style: 'currency',
          currency: m.code,
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(Number(amount || 0));
      } catch {
        return `${m.symbol}${Number(amount || 0).toFixed(decimals)}`;
      }
    };

    return {
      displayCcy: ccy,
      setDisplayCcy,
      rates,
      meta,
      loaded,
      convert,
      format,
      formatSigned,
      formatNative,
      symbol: meta.symbol,
    };
  }, [displayCcy, rates, loaded, setDisplayCcy]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
};
